import {
  type PdfPageBaseLayout,
  FontAwesomeIcon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import {
  getFieldKey,
  getFieldText,
  isSignableField,
  isSignerFillableField,
} from "@/pages/esign/esign-sign.util.ts";
import {
  fieldTypeConfig,
  getFieldFontSize,
  getFieldFontSizeCqh,
  getFieldPositionStyle,
  usesHandwritingFont,
} from "@/pages/esign/esign.config.ts";
import { ApiEsignField, EsignFieldType } from "@/types/esign.types.ts";
import { type RefObject, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

function FieldValue({
  text,
  handwriting,
  fontSize,
}: {
  text: string;
  handwriting: boolean;
  fontSize: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const fit = () => {
      el.style.transform = "scale(1)";
      const style = getComputedStyle(parent);
      const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      const available = parent.clientWidth - padX;
      const width = el.scrollWidth;
      const scale = width > available && width > 0 ? available / width : 1;
      el.style.transform = `scale(${scale})`;
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [text]);

  return (
    <span
      ref={ref}
      className={
        handwriting
          ? "whitespace-nowrap leading-none font-signature"
          : "whitespace-nowrap leading-none"
      }
      style={{
        fontSize,
        transformOrigin: "center",
      }}
    >
      {text}
    </span>
  );
}

function useFitFont(
  ref: RefObject<HTMLElement | null>,
  value: string,
  baseCqh: number,
  axis: "width" | "height"
) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const fit = () => {
      el.style.fontSize = `${baseCqh}cqh`;

      if (axis === "width") {
        if (el.scrollWidth > el.clientWidth && el.scrollWidth > 0) {
          el.style.fontSize = `${baseCqh * (el.clientWidth / el.scrollWidth)}cqh`;
        }
        return;
      }

      if (el.scrollHeight <= el.clientHeight) return;
      let lo = 0;
      let hi = baseCqh;
      for (let i = 0; i < 10; i++) {
        const mid = (lo + hi) / 2;
        el.style.fontSize = `${mid}cqh`;
        if (el.scrollHeight <= el.clientHeight) lo = mid;
        else hi = mid;
      }
      el.style.fontSize = `${lo}cqh`;
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, value, baseCqh, axis]);
}

function AutoFitTextarea({
  value,
  placeholder,
  onChange,
  baseCqh,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  baseCqh: number;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useFitFont(ref, value, baseCqh, "height");
  return (
    <textarea
      ref={ref}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={
        "h-full w-full resize-none bg-transparent px-1 text-center leading-tight outline-none placeholder:text-cyan"
      }
      style={{ fontSize: `${baseCqh}cqh` }}
    />
  );
}

function AutoFitInput({
  value,
  placeholder,
  onChange,
  baseCqh,
}: {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  baseCqh: number;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useFitFont(ref, value, baseCqh, "width");
  return (
    <input
      ref={ref}
      type={"text"}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={
        "h-full w-full bg-transparent px-1 text-center leading-none outline-none placeholder:text-cyan"
      }
      style={{ fontSize: `${baseCqh}cqh` }}
    />
  );
}

function FillableFieldControl({
  field,
  value,
  onChange,
}: {
  field: ApiEsignField;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const baseLabel = field.label ?? t(fieldTypeConfig[field.field_type].labelKey);
  const placeholder = field.is_required ? `${baseLabel} *` : baseLabel;
  const baseCqh = getFieldFontSizeCqh(field);

  if (field.field_type === EsignFieldType.yes_no) {
    return (
      <div className={"flex h-full w-full items-stretch gap-0.5 p-0.5"}>
        {["yes", "no"].map((option) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type={"button"}
              onClick={() => onChange(selected ? "" : option)}
              className={
                selected
                  ? "flex flex-1 items-center justify-center rounded-xs bg-cyan text-light"
                  : "flex flex-1 items-center justify-center rounded-xs text-secondary hover:bg-cyan/10"
              }
              style={{ fontSize: getFieldFontSize(field) }}
            >
              {option === "yes" ? t("common:yes") : t("common:no")}
            </button>
          );
        })}
      </div>
    );
  }

  if (field.is_multiline) {
    return (
      <AutoFitTextarea
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        baseCqh={baseCqh}
      />
    );
  }

  return (
    <AutoFitInput value={value} placeholder={placeholder} onChange={onChange} baseCqh={baseCqh} />
  );
}

export interface EsignSignFieldsLayerProps {
  baseLayout: PdfPageBaseLayout;
  fields: ApiEsignField[];
  disabledFields?: ApiEsignField[];
  signedKeys: Set<string>;
  values: Record<string, string>;
  onSign: (key: string) => void;
  onChangeValue: (key: string, value: string) => void;
  registerFieldRef: (key: string, el: HTMLElement | null) => void;
}

export function EsignSignFieldsLayer({
  baseLayout,
  fields,
  disabledFields = [],
  signedKeys,
  values,
  onSign,
  onChangeValue,
  registerFieldRef,
}: EsignSignFieldsLayerProps) {
  const { t } = useTranslation();

  if (fields.length === 0 && disabledFields.length === 0) return null;

  return (
    <div className={"absolute inset-0"}>
      {disabledFields.map((field) => {
        return (
          <div
            key={`disabled-${getFieldKey(field)}`}
            style={{
              ...getFieldPositionStyle(field, baseLayout.width, baseLayout.height),
              containerType: "size",
            }}
            className={
              "absolute flex items-center justify-center overflow-hidden rounded-sm border border-dark/10 px-1"
            }
          >
            <FieldValue
              text={getFieldText(field, t)}
              handwriting={usesHandwritingFont(field.field_type)}
              fontSize={getFieldFontSize(field)}
            />
          </div>
        );
      })}
      {fields.map((field) => {
        const key = getFieldKey(field);
        const positionStyle = getFieldPositionStyle(field, baseLayout.width, baseLayout.height);

        if (isSignerFillableField(field)) {
          const value = values[key] ?? "";
          const borderClass = value.trim() !== "" ? "border-dark/20" : "border-cyan bg-cyan/10";

          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild={true}>
                <div
                  ref={(el) => registerFieldRef(key, el)}
                  style={{ ...positionStyle, containerType: "size" }}
                  className={`absolute overflow-hidden rounded-xs border ${borderClass}`}
                >
                  <FillableFieldControl
                    field={field}
                    value={value}
                    onChange={(next) => onChangeValue(key, next)}
                  />
                </div>
              </TooltipTrigger>
              {field.label && (
                <TooltipContent>
                  <Text className={"text-light"}>
                    {field.is_required ? `${field.label} *` : field.label}
                  </Text>
                </TooltipContent>
              )}
            </Tooltip>
          );
        }

        const signable = isSignableField(field);
        const isFilled = !signable || signedKeys.has(key);
        const handwriting = usesHandwritingFont(field.field_type);

        if (!isFilled) {
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild={true}>
                <button
                  ref={(el) => registerFieldRef(key, el)}
                  type={"button"}
                  onClick={() => onSign(key)}
                  style={{ ...positionStyle, containerType: "size" }}
                  className={
                    "group/sign absolute flex cursor-pointer items-center justify-center rounded-xs border border-cyan bg-cyan/10 p-2.5 text-cyan transition-colors hover:bg-cyan/20"
                  }
                >
                  <span
                    className={
                      "title-sub-headline pointer-events-none truncate text-[11px] leading-none group-hover/sign:opacity-0"
                    }
                  >
                    {t(fieldTypeConfig[field.field_type].labelKey)}
                  </span>
                  <span
                    className={
                      "pointer-events-none absolute inset-0 flex items-center justify-center px-1 text-dark opacity-0 group-hover/sign:opacity-100"
                    }
                  >
                    <FieldValue
                      text={getFieldText(field, t)}
                      handwriting={handwriting}
                      fontSize={getFieldFontSize(field)}
                    />
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <Text className={"text-light"}>{t("core:esign_click_to_sign")}</Text>
              </TooltipContent>
            </Tooltip>
          );
        }

        return (
          <div
            key={key}
            ref={(el) => registerFieldRef(key, el)}
            style={positionStyle}
            className={"absolute"}
          >
            <div
              style={{ containerType: "size" }}
              className={
                "flex h-full w-full items-center justify-center overflow-hidden rounded-sm border border-dark/10 px-1"
              }
            >
              <FieldValue
                text={getFieldText(field, t)}
                handwriting={handwriting}
                fontSize={getFieldFontSize(field)}
              />
            </div>
            {signable && (
              <FontAwesomeIcon
                icon={"fa-light fa-check"}
                className={"absolute -top-0.5 -left-0.5 bg-light leading-none text-dark"}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
